import '../../styles/storyDetail.css';
import useStoryChatter from '../../hooks/useStoryChatter';
import ChatterPagination from './ChatterPagination';
import { displayLabel } from '../../utils/displayLabels';
import { isAwaitingFeedback, TEST_OVERDUE_LABEL } from '../../utils/overdue';
import { useDynamicsRecordLink } from '../../hooks/useDynamicsRecordLink';
import { useDynamicsPage } from '../../hooks/useDynamicsPage';
import ClientFilters from '../common/ClientFilters';
import ClientPage from '../common/ClientPage';
import ThemeToggle from '../common/ThemeToggle';
import FilterSelectionCard from '../common/FilterSelectionCard';
import { buildStoryPointRangeOptions, matchesStoryPointRanges, sortMilestoneOptionsByDeadline } from '../../utils/dashboardFilters';
import { loadDashboardFilter, saveDashboardFilter } from '../../utils/savedDashboardFilters';
import UserStoryBillingBadge from './UserStoryBillingBadge';
import AcceptanceCriteriaGrid from './AcceptanceCriteriaGrid';
import useAnimatedModalClose from '../../hooks/useAnimatedModalClose';
/**
 * Page User Stories par Statut
 *
 * Affiche la liste des User Stories d'un statut donné (Backlog, En Test, Done)
 * Dans le même style que la page Test Client
 */

import React, { useState, useMemo, useEffect, useCallback } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { ArrowLeft, Search, XCircle, MessageSquare, Send, User, CheckCircle, AlertCircle, ArrowUpDown, ArrowUp, ArrowDown, Zap, Clock, TrendingUp, CalendarDays, BarChart2, ChevronDown, ChevronRight } from 'lucide-react';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';
import { useProjectData } from "../../context/ProjectDataContext";
import { useAuth } from '../../context/AuthContext';
import UserStoryAttachments from './UserStoryAttachments';
import LoadingSpinner from '../common/LoadingSpinner';
import ErrorMessage from '../common/ErrorMessage';
import Pagination from '../common/Pagination';
import backendApi from '../../services/backendApi';
import '../../styles/odooDescription.css';
import '../../styles/userStoryFilters.css';
import '../../styles/ticketSituation.css';

// ── Helpers groupement par date (hors composant = références stables) ─────
const MONTHS_FR = ['Janvier','Février','Mars','Avril','Mai','Juin','Juillet','Août','Septembre','Octobre','Novembre','Décembre'];
const MONTHS_SHORT = ['jan','fév','mar','avr','mai','jun','jul','aoû','sep','oct','nov','déc'];

const getUsDate = (us, field) => {
  const raw = us[field];
  const date = raw ? new Date(raw) : null;
  return date && !Number.isNaN(date.getTime()) ? date : null;
};

const getPeriodInfo = (date, mode) => {
  if (!date) return { key: 'unknown', label: 'Date inconnue', sortKey: '0000' };
  if (mode === 'year') {
    const key = String(date.getFullYear());
    return { key, label: key, sortKey: key };
  }
  if (mode === 'day') {
    const key = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`;
    return { key, label: date.toLocaleDateString('fr-FR'), sortKey: key };
  }
  if (mode === 'month') {
    const y = date.getFullYear(), m = date.getMonth();
    const k = `${y}-${String(m + 1).padStart(2, '0')}`;
    return { key: k, label: `${MONTHS_FR[m]} ${y}`, sortKey: k };
  }
  const d = new Date(date); d.setHours(12, 0, 0, 0);
  d.setDate(d.getDate() + 3 - (d.getDay() + 6) % 7);
  const jan4 = new Date(d.getFullYear(), 0, 4);
  const wk = 1 + Math.round(((d - jan4) / 86400000 - 3 + (jan4.getDay() + 6) % 7) / 7);
  const ws = new Date(date); ws.setDate(date.getDate() - (date.getDay() + 6) % 7);
  const we = new Date(ws); we.setDate(ws.getDate() + 6);
  const fmt = (dt) => `${dt.getDate()} ${MONTHS_SHORT[dt.getMonth()]}`;
  const k = `${d.getFullYear()}-W${String(wk).padStart(2, '0')}`;
  return { key: k, label: `Sem. ${wk} · ${fmt(ws)} – ${fmt(we)}`, sortKey: k };
};

const UserStoriesByStatusPage = ({ situation = false }) => {
  const { user } = useAuth();
  const [creator, setCreator] = useState('all');
  const location = useLocation();
  const overdueOnly = new URLSearchParams(location.search).get('overdue') === '1';
  const initialStage = new URLSearchParams(location.search || '').get('stage') || 'all';
  const [stage, setStage] = useState(initialStage);
  useEffect(() => { setStage(initialStage); }, [initialStage]);
  const [billing, setBilling] = useState('all');
  const [filtersOpen, setFiltersOpen] = useState(false);
  const navigate = useNavigate();

  // Récupérer les paramètres passés depuis la navigation
  const { status, statusLabel } = situation ? { status: 'All', statusLabel: 'Situation des tickets' } : location.state || {status:'All', statusLabel:'User Stories'};

  const showDoneLayout = situation || status === 'Done' || status === 'All';

  const [selectedMilestone, setSelectedMilestone] = useState('all');
  const [selectedMilestones, setSelectedMilestones] = useState(new Set());
  const [searchQuery, setSearchQuery] = useState('');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [dateField, setDateField] = useState('dateBacklog');
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    if (params.get('dynamics') !== '1') return;
    setSearchQuery(params.get('search') || '');
    setDateFrom(params.get('dateFrom') || '');
    setDateTo(params.get('dateTo') || '');
    setDateField(params.get('dateField') === 'dateDone' ? 'dateDone' : 'dateBacklog');
    setSelectedMilestone('all'); setSelectedMilestones(new Set()); setCreator('all'); setBilling('all'); setSelectedSPs(new Set()); setSelectedSPRanges(new Set()); setCurrentPage(1);
  }, [location.search]);
  const [sortSP, setSortSP] = useState('none');
  const [sortDate, setSortDate] = useState('desc');
  const [selectedSPs, setSelectedSPs] = useState(new Set());
  const [selectedSPRanges, setSelectedSPRanges] = useState(new Set());
  const [groupDateField, setGroupDateField] = useState('dateDone');
  const [groupBy, setGroupBy] = useState('none'); // 'none' | 'month' | 'week'
  const [filterSaveState, setFilterSaveState] = useState('idle');
  const [hasSavedFilter, setHasSavedFilter] = useState(false);
  const [collapsedGroups, setCollapsedGroups] = useState(new Set());
  const [viewingUs, setViewingUs] = useState(null); // US en cours de visualisation
  const [currentPage, setCurrentPage] = useState(1);

  // États chatter (utilisés uniquement quand true)
  const [isSendingUserStoryMessage, setIsSendingUserStoryMessage] = useState(false);
  const [newUserStoryMessage, setNewUserStoryMessage] = useState('');
  const itemsPerPage = 20; // Nombre d'US par page
  const [validatingId, setValidatingId] = useState(null); // ID de l'US en cours de validation
  const [validatedIds, setValidatedIds] = useState(new Set()); // IDs des US déjà validées
  const [rejectingUs, setRejectingUs] = useState(null); // US en cours de refus (modale)
  const [rejectFeedback, setRejectFeedback] = useState(''); // Motif de refus
  const [rejectingId, setRejectingId] = useState(null); // ID de l'US en cours d'envoi refus
  const [rejectedIds, setRejectedIds] = useState(new Set()); // IDs des US refusées

  // Lire les données depuis le contexte partagé (pas de nouvel appel API)
  const {
    userStories,
    milestones = [],
    isAdmin = false,
    loading,
    error: dataError,
    refresh,
  } = useProjectData();

  useEffect(() => {
    if (!isAdmin) setBilling('all');
  }, [isAdmin]);

  const applySavedFilter = useCallback(saved => {
    setSearchQuery(typeof saved.searchQuery === 'string' ? saved.searchQuery : '');
    setSelectedMilestones(new Set(Array.isArray(saved.milestones) ? saved.milestones : []));
    setSelectedSPRanges(new Set(Array.isArray(saved.storyPointRanges) ? saved.storyPointRanges : []));
    setCreator(saved.creator ?? 'all');
    setStage(saved.stage ?? 'all');
    setBilling(saved.billing ?? 'all');
    setGroupBy(['none', 'day', 'week', 'month', 'year'].includes(saved.groupBy) ? saved.groupBy : 'none');
    setGroupDateField(saved.groupDateField === 'dateBacklog' ? 'dateBacklog' : 'dateDone');
  }, []);

  useEffect(() => {
    if (!situation) return;
    const saved = loadDashboardFilter('ticket-situation');
    setHasSavedFilter(Boolean(saved));
    const params = new URLSearchParams(location.search);
    const hasExplicitRouteFilter = initialStage !== 'all' || overdueOnly || params.has('openRecord') || params.get('dynamics') === '1';
    if (saved && !hasExplicitRouteFilter) applySavedFilter(saved);
  }, [applySavedFilter, initialStage, location.search, overdueOnly, situation]);

  // Filtrer les US selon le statut
  const statusStories = useMemo(() => {
    if (situation) return userStories;
    if (status === 'All') return userStories;
    if (!status) return [];
    if (status === 'Confirmation') {
      return userStories.filter(us => (us.category === 'Confirmation' || us.stageId === 397) && (!overdueOnly || (us.category === 'Confirmation' && (isAdmin || us.canDecide) && isAwaitingFeedback(us, us.dateStageEntered))));
    }
    return userStories.filter(us => us.category === status);
  }, [userStories, status, situation, isAdmin, overdueOnly]);

  // Valeurs SP uniques disponibles
  const uniqueSPs = useMemo(() => {
    const vals = new Set(statusStories.map(us => us.storyPoints || 0));
    return [...vals].sort((a, b) => a - b);
  }, [statusStories]);
  const storyPointRangeOptions = useMemo(() => buildStoryPointRangeOptions(statusStories), [statusStories]);

  const toggleSP = (sp) => {
    setSelectedSPs(prev => {
      const next = new Set(prev);
      if (next.has(sp)) next.delete(sp); else next.add(sp);
      return next;
    });
  };
  const toggleSPRange = useCallback(range => {
    setSelectedSPRanges(current => { const next = new Set(current); if (next.has(range)) next.delete(range); else next.add(range); return next; });
  }, []);

  // Liste des Jalons avec leur nombre d'US
  const jalonesWithCount = useMemo(() => {
    const milestoneCounts = {};

    statusStories.forEach(us => {
      const milestoneName = us.milestoneName || 'Sans Jalon';
      milestoneCounts[milestoneName] = (milestoneCounts[milestoneName] || 0) + 1;
    });

    return Object.entries(milestoneCounts).map(([name, count]) => ({ name, count }));
  }, [statusStories]);
  const sortedMilestones = useMemo(() => sortMilestoneOptionsByDeadline(jalonesWithCount, milestones), [jalonesWithCount, milestones]);

  // Filtrer les US selon le Jalon sélectionné et la recherche
  const filteredStories = useMemo(() => {
    let filtered = statusStories;
    if (dateFrom || dateTo) filtered = filtered.filter(us => {
      const date = us[dateField]?.slice(0, 10);
      return date && (!dateFrom || date >= dateFrom) && (!dateTo || date <= dateTo);
    });
    if (creator !== 'all') filtered = filtered.filter(us => String(us.creatorId) === (creator === 'me' ? String(user?.uid) : creator));
    if (stage !== 'all') filtered = filtered.filter(us => (us.stageName || 'Sans étape') === stage || us.category === stage);
    if (situation && overdueOnly) filtered = filtered.filter(us => us.category === 'Confirmation' && (isAdmin || us.canDecide) && isAwaitingFeedback(us, us.dateStageEntered));
    if (isAdmin && billing !== 'all') filtered = filtered.filter(us => !!us.isBilled === (billing === 'billed'));

    // Filtre par Jalon
    if (situation && selectedMilestones.size > 0) {
      filtered = filtered.filter(us => selectedMilestones.has(us.milestoneName || 'Sans Jalon'));
    } else if (selectedMilestone !== 'all') {
      filtered = filtered.filter(us => {
        const milestoneName = us.milestoneName || 'Sans Jalon';
        return milestoneName === selectedMilestone;
      });
    }

    // Filtre par recherche
    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase();
      filtered = filtered.filter(us => {
        const name = us.name?.toLowerCase() || '';
        const description = us.description?.toLowerCase() || '';
        const code = us.code?.toLowerCase() || '';

        return name.includes(query) || description.includes(query) || code.includes(query);
      });
    }

    // Filtre par Story Points
    if (situation && selectedSPRanges.size > 0) {
      filtered = filtered.filter(us => matchesStoryPointRanges(us.storyPoints, selectedSPRanges));
    } else if (selectedSPs.size > 0) {
      filtered = filtered.filter(us => selectedSPs.has(us.storyPoints || 0));
    }

    if (sortSP === 'asc') {
      filtered = [...filtered].sort((a, b) => (a.storyPoints || 0) - (b.storyPoints || 0));
    } else if (sortSP === 'desc') {
      filtered = [...filtered].sort((a, b) => (b.storyPoints || 0) - (a.storyPoints || 0));
    } else {
      // Tri date done par défaut (desc = plus récent en premier)
      filtered = [...filtered].sort((a, b) => {
        const da = a.dateDone ? new Date(a.dateDone).getTime() : 0;
        const db = b.dateDone ? new Date(b.dateDone).getTime() : 0;
        return sortDate === 'desc' ? db - da : da - db;
      });
    }

    return filtered;
  }, [dateFrom, dateTo, dateField, statusStories, selectedMilestone, selectedMilestones, situation, overdueOnly, searchQuery, sortSP, sortDate, selectedSPs, selectedSPRanges, creator, stage, billing, isAdmin, user?.uid]);

  // KPI calculés sur toutes les US filtrées, avant pagination.
  const doneKpis = useMemo(() => {
    if (!showDoneLayout) return null;
    const total = filteredStories.length;
    const totalSP = filteredStories.reduce((s, us) => s + (us.storyPoints || 0), 0);
    const totalHours = filteredStories.reduce((s, us) => s + (us.hourlyRate || 0), 0);
    const avgSP = total > 0 ? Math.round((totalSP / total) * 10) / 10 : 0;
    return { total, totalSP, totalHours, avgSP };
  }, [filteredStories, showDoneLayout]);

  const stageViews = useMemo(() => {
    const counts = new Map();
    statusStories.forEach(us => { const name = us.stageName || us.category || 'Sans étape'; counts.set(name, (counts.get(name) || 0) + 1); });
    return [{ value: 'all', label: 'Toutes', count: statusStories.length }, ...[...counts.entries()].map(([name, count]) => ({ value: name, label: displayLabel(name), count }))];
  }, [statusStories]);

  const toggleSituationMilestone = useCallback(name => {
    setSelectedMilestones(current => { const next = new Set(current); if (next.has(name)) next.delete(name); else next.add(name); return next; });
  }, []);

  const resetFilters = useCallback(() => {
    setDateFrom(''); setDateTo(''); setSearchQuery(''); setSelectedMilestone('all'); setSelectedMilestones(new Set());
    setCreator('all'); setStage('all'); setBilling('all'); setSelectedSPs(new Set()); setSelectedSPRanges(new Set());
  }, []);

  const saveCurrentFilter = useCallback(() => {
    const saved = saveDashboardFilter('ticket-situation', {
      searchQuery,
      milestones: [...selectedMilestones],
      storyPointRanges: [...selectedSPRanges],
      creator,
      stage,
      billing,
      groupBy,
      groupDateField,
    });
    if (saved) setHasSavedFilter(true);
    setFilterSaveState(saved ? 'saved' : 'error');
    window.setTimeout(() => setFilterSaveState('idle'), 1800);
  }, [billing, creator, groupBy, groupDateField, searchQuery, selectedMilestones, selectedSPRanges, stage]);
  const reloadSavedFilter = useCallback(() => {
    const saved = loadDashboardFilter('ticket-situation');
    if (saved) applySavedFilter(saved);
  }, [applySavedFilter]);

  // Groupement commun après filtrage.
  const groupedStories = useMemo(() => {
    if (!showDoneLayout || groupBy === 'none') return null;
    const groups = {};
    filteredStories.forEach(us => {
      const date = getUsDate(us, groupDateField);
      const { key, label, sortKey } = getPeriodInfo(date, groupBy);
      if (!groups[key]) groups[key] = { key, label, sortKey, stories: [], totalSP: 0, totalHours: 0 };
      groups[key].stories.push(us);
      groups[key].totalSP += us.storyPoints || 0;
      groups[key].totalHours += us.hourlyRate || 0;
    });
    return Object.values(groups).sort((a, b) => b.sortKey.localeCompare(a.sortKey));
  }, [filteredStories, showDoneLayout, groupBy, groupDateField]);

  // Données du graphique (chronologique)
  const chartData = useMemo(() => {
    if (!showDoneLayout || groupBy === 'none') return [];
    const base = groupedStories ? [...groupedStories].filter(g => g.key !== 'unknown') : [];
    return base.sort((a, b) => a.sortKey.localeCompare(b.sortKey))
      .map(g => ({ name: g.label, sp: g.totalSP, hours: g.totalHours, us: g.stories.length }));
  }, [groupedStories, showDoneLayout, groupBy]);

  useEffect(() => {
    setCollapsedGroups(new Set());
  }, [groupBy, groupDateField]);

  const toggleGroup = (key) => {
    setCollapsedGroups(prev => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key); else next.add(key);
      return next;
    });
  };

  /**
   * Réinitialiser la page lors du changement de filtres
   */
  useEffect(() => {
    setCurrentPage(1);
  }, [dateFrom, dateTo, dateField, selectedMilestone, selectedMilestones, searchQuery, sortSP, sortDate, selectedSPs, selectedSPRanges, creator, stage, billing]);

  /**
   * Pagination : calculer les US à afficher pour la page courante
   */
  const paginatedStories = useMemo(() => {
    const startIndex = (currentPage - 1) * itemsPerPage;
    const endIndex = startIndex + itemsPerPage;
    return filteredStories.slice(startIndex, endIndex);
  }, [filteredStories, currentPage, itemsPerPage]);

  const totalPages = Math.ceil(filteredStories.length / itemsPerPage);

  // Charger les messages du chatter quand une US est sélectionnée (statut "En Test")
  const chatter = useStoryChatter(viewingUs?.id);
  const { messages: userStoryMessages, loading: loadingUsMessages, reload: fetchUserStoryMessages } = chatter;

  // Envoyer un message dans le chatter
  const handleSendUserStoryMessage = async (e) => {
    e.preventDefault();
    if (!newUserStoryMessage.trim() || !viewingUs) return;

    setIsSendingUserStoryMessage(true);
    try {
      await backendApi.createUserStoryMessage(viewingUs.id, newUserStoryMessage);
      setNewUserStoryMessage('');
      await fetchUserStoryMessages(viewingUs.id);
    } catch (error) {
      console.error('Erreur envoi message US:', error);
      alert(error.message || 'Erreur lors de l\'envoi du message');
    } finally {
      setIsSendingUserStoryMessage(false);
    }
  };

  /**
   * Ouvrir la modale de détails
   */
  const openViewModal = (us) => {
    setViewingUs(us);
  };

  /**
   * Fermer la modale de détails
   */
  const closeViewModal = () => {
    setViewingUs(null);
    setNewUserStoryMessage('');
  };

  // Accepter une US refusée : retour en Confirmation + tag "Validé par le client"
  const handleAcceptRefused = useCallback(async (e, us) => {
    e.stopPropagation();
    if (validatingId || !(isAdmin || us.canDecide)) return;

    const confirmationStageId = statusStories.find(s => s.stageId !== 397)?.stageId;
    if (!confirmationStageId) {
      alert('Impossible de trouver le stage Confirmation.');
      return;
    }

    setValidatingId(us.id);
    try {
      await backendApi.updateTaskStage(us.id, confirmationStageId);
      await backendApi.validateUserStory(us.id);
      setRejectedIds(prev => { const s = new Set(prev); s.delete(us.id); return s; });
      setValidatedIds(prev => new Set(prev).add(us.id));
      await refresh();
    } catch (error) {
      console.error('Erreur acceptation US refusée:', error);
      alert(error.message || 'Erreur lors de l\'acceptation');
    } finally {
      setValidatingId(null);
    }
  }, [validatingId, statusStories, refresh, isAdmin]);

  // Valider / dévalider une US
  const handleToggleValidation = useCallback(async (e, usId) => {
    e.stopPropagation();
    if (validatingId || !(isAdmin || userStories.some(us => us.id === usId && us.canDecide))) return;

    const isValidated = validatedIds.has(usId) || userStories.some(us => us.id === usId && us.clientValidated);
    if (isValidated) return;
    setValidatingId(usId);
    try {
      await backendApi.validateUserStory(usId);
      setValidatedIds(prev => new Set(prev).add(usId));
    } catch (error) {
      console.error('Erreur validation US:', error);
      alert(error.message || 'Erreur lors de la validation');
    } finally {
      setValidatingId(null);
    }
  }, [validatingId, validatedIds, isAdmin, userStories]);

  // Ouvrir la modale de refus
  const openRejectModal = (e, us) => {
    e.stopPropagation();
    if (!(isAdmin || us.canDecide)) return;
    setRejectingUs(us);
    setRejectFeedback('');
  };

  const closeRejectModal = () => {
    setRejectingUs(null);
    setRejectFeedback('');
  };
  const viewModalExit = useAnimatedModalClose(closeViewModal);
  const rejectModalExit = useAnimatedModalClose(closeRejectModal, { disabled: rejectingId !== null });

  // Confirmer le refus
  const handleConfirmReject = async () => {
    if (!rejectFeedback.trim() || !rejectingUs || !(isAdmin || rejectingUs.canDecide)) return;
    setRejectingId(rejectingUs.id);
    try {
      await backendApi.updateTaskStage(rejectingUs.id, 397, rejectFeedback.trim());
      setRejectedIds(prev => new Set(prev).add(rejectingUs.id));
      rejectModalExit.close();
    } catch (error) {
      console.error('Erreur refus US:', error);
      alert(error.message || 'Erreur lors du refus');
    } finally {
      setRejectingId(null);
    }
  };

  // Afficher le spinner de chargement
  useDynamicsRecordLink(userStories, openViewModal);
  useDynamicsPage({path:situation ? '/dashboard/ticket-situation' : '/dashboard/user-stories-status',title:viewingUs ? viewingUs.name : (statusLabel || status),kind:'tasks',recordIds:[...new Set([...paginatedStories.map(t => t.id), ...(viewingUs ? [viewingUs.id] : [])])],listRecordIds:paginatedStories.map(t => t.id),focusedRecordId:viewingUs?.id || null,filters:{dateFrom,dateTo,dateField,search:searchQuery, milestone:situation ? [...selectedMilestones] : selectedMilestone, stage, creator, storyPoints:situation ? [...selectedSPRanges] : [...selectedSPs], ...(isAdmin ? {billing} : {}), groupBy, groupDateField},ready:!loading});
  const resultsAnimationKey = [
    currentPage, groupBy, groupDateField, searchQuery, dateFrom, dateTo, dateField,
    selectedMilestone, [...selectedMilestones].join(','), stage, creator, billing,
    [...selectedSPs].join(','), [...selectedSPRanges].join(','), filteredStories.length,
    paginatedStories.map(story => story.id).join(','),
  ].join('|');

  if (loading) {
    return (
      <div className="min-h-full bg-gray-50">
        <LoadingSpinner message="Chargement des User Stories..." />
      </div>
    );
  }

  // Afficher le message d'erreur
  if (dataError) {
    return (
      <div className="min-h-full bg-gray-50">
        <ErrorMessage message={dataError} onRetry={refresh} />
      </div>
    );
  }

  return (
    <ClientPage>
      <div className={`client-story-page${situation ? ` ticket-situation${filtersOpen ? ' filters-open' : ''}` : ''}`}>
        <div className="client-theme-control"><ThemeToggle /></div>
        {/* Titre */}
        <div className={`client-story-header mb-6${situation ? ' ticket-situation__header' : ''}`}>
          <div className="flex flex-wrap items-center justify-between gap-4">
            {/* Partie gauche : Bouton retour + Titre */}
            <div className="flex items-center gap-4">
              {!situation && <button
                onClick={() => navigate(-1)}
                className="p-2 hover:bg-gray-200 rounded-lg transition-colors"
              >
                <ArrowLeft className="w-5 h-5 text-gray-600" />
              </button>}
              <div>
                {situation && <p className="ticket-situation__eyebrow">Suivi du projet</p>}
                <h1 className="text-2xl font-semibold text-gray-900 mb-1">
                  {status === 'Confirmation' ? 'Confirmation' : statusLabel || status}
                </h1>
                {!situation && <p className="text-sm text-gray-500">
                  {filteredStories.length} user {filteredStories.length > 1 ? 'stories' : 'story'}
                  {selectedMilestone !== 'all' ? ` dans ${selectedMilestone}` : ''}
                </p>}
              </div>
            </div>

          </div>
        </div>

        {situation && <div className="ticket-situation__views" aria-label="Vues rapides">
          {stageViews.map(view => <button key={view.value} type="button" aria-pressed={stage === view.value} onClick={() => setStage(view.value)}>
            {view.label}<span>{view.count}</span>
          </button>)}
        </div>}

        {overdueOnly && <p role="status">{TEST_OVERDUE_LABEL.replace('mise à disposition', 'demande de confirmation')} <button type="button" onClick={() => navigate(location.pathname, { state: location.state })}>Afficher toutes les confirmations</button></p>}
        {!situation && <ClientFilters title={status === 'Confirmation' ? 'Confirmation' : statusLabel || status} count={`${filteredStories.length} US`} summary={[(dateFrom || dateTo) && `${dateField === 'dateDone' ? 'Date done' : 'Date backlog'} : ${dateFrom || '…'} → ${dateTo || '…'}`, isAdmin && billing !== 'all' && (billing === 'billed' ? 'Facturées' : 'Non facturées'), selectedSPs.size > 0 && `${[...selectedSPs].join(', ')} SP`, groupBy !== 'none' && `Regroupées par ${{day:'jour',week:'semaine',month:'mois',year:'année'}[groupBy] || groupBy} · ${groupDateField === 'dateBacklog' ? 'Date backlog' : 'Date done'}`]} primary={<section aria-label="Filtres principaux" className="us-filters">          <div className={`us-filters__fields ${status === 'All' ? '' : 'us-filters__fields--simple'}`}>
            <div className="us-filters__field">
              <label htmlFor="us-search-filter">Recherche</label>
            <div className="us-filters__search">
              <Search size={19} aria-hidden="true" />
              <input id="us-search-filter" aria-label="Rechercher une User Story" type="search" placeholder="Rechercher par titre, description ou code..." value={searchQuery} onChange={e => setSearchQuery(e.target.value)} />
            </div>
            </div>
            <div className="us-filters__field">
              <label htmlFor="milestone-filter">Jalon</label>
              <select id="milestone-filter" value={selectedMilestone} onChange={e => setSelectedMilestone(e.target.value)}>
                <option value="all">Tous les jalons ({statusStories.length})</option>
                {jalonesWithCount.map(({ name, count }) => <option key={name} value={name}>{name} ({count})</option>)}
              </select>
            </div>
            {(situation || status === 'All') && <>
              <div className="us-filters__field">
                <label htmlFor="us-stage-filter">Étape</label>
                <select id="us-stage-filter" value={stage} onChange={e => setStage(e.target.value)}>
                  <option value="all">Toutes les étapes</option>
                  {[...new Set(userStories.map(us => us.stageName).filter(Boolean))].map(name => <option key={name} value={name}>{displayLabel(name)}</option>)}
                </select>
              </div>
              <div className="us-filters__field">
                <label htmlFor="us-creator-filter">Créateur</label>
                <select id="us-creator-filter" value={creator} onChange={e => setCreator(e.target.value)}>
                  <option value="all">Tous les créateurs</option><option value="me">Créées par moi</option>
                  {[...new Map(userStories.filter(us => us.creatorId).map(us => [us.creatorId, us.creatorName])).entries()].map(([id, name]) => <option key={id} value={id}>{name}</option>)}
                </select>
              </div>
            </>}
          </div></section>}>
<section aria-label="Filtres avancés et regroupement" className="us-filters">
          <div className="flex flex-wrap gap-4 mb-4"><label className="us-filters__field">Date de période<select value={dateField} onChange={e => setDateField(e.target.value)}><option value="dateBacklog">Date backlog</option><option value="dateDone">Date done</option></select></label>
          <label className="us-filters__field">Du<input type="date" value={dateFrom} onChange={e => setDateFrom(e.target.value)} /></label>
          <label className="us-filters__field">Au<input type="date" value={dateTo} onChange={e => setDateTo(e.target.value)} /></label></div>          <div className="us-filters__bottom">
          {isAdmin && <div className="us-filters__field shrink-0">
            <label htmlFor="us-billing-filter">Facturation</label>
            <select id="us-billing-filter" value={billing} onChange={e => setBilling(e.target.value)}>
              <option value="all">Toutes</option>
              <option value="billed">Facturées</option>
              <option value="unbilled">Non facturées</option>
            </select>
          </div>}
          {uniqueSPs.length > 1 && <div className="us-filters__points">
            <span>Story Points :</span>
            <div role="group" aria-label="Filtrer par Story Points" className="us-filters__chips">
              {uniqueSPs.map(sp => <button key={sp} type="button" aria-pressed={selectedSPs.has(sp)} onClick={() => toggleSP(sp)}>{sp} SP</button>)}
              {selectedSPs.size > 0 && <button type="button" className="us-filters__clear" onClick={() => setSelectedSPs(new Set())}>Effacer</button>}
            </div>
          </div>}

          {showDoneLayout && <div className="us-filters__grouping">
            <div className="us-filters__period">
              <span id="group-period-label"><BarChart2 size={16} aria-hidden="true" />Regrouper par</span>
              <div role="group" aria-labelledby="group-period-label" className="us-filters__segments">
                {[{ value: 'none', label: 'Aucun' }, { value: 'day', label: 'Jour' }, { value: 'week', label: 'Semaine' }, { value: 'month', label: 'Mois' }, { value: 'year', label: 'Année' }].map(opt => (
                  <button key={opt.value} type="button" onClick={() => setGroupBy(opt.value)} aria-pressed={groupBy === opt.value}>{opt.label}</button>
                ))}
              </div>
            </div>
            {groupBy !== 'none' && <div className="us-filters__date">
              <label htmlFor="group-date-field">À partir de</label>
              <select id="group-date-field" aria-label="Date de regroupement" value={groupDateField} onChange={e => setGroupDateField(e.target.value)}>
                <option value="dateBacklog">Date backlog</option><option value="dateDone">Date done</option>
              </select>
            </div>}
          </div>}
          </div>
                    <button type="button" className="us-filters__reset"
              disabled={!dateFrom && !dateTo && !searchQuery && selectedMilestone === 'all' && creator === 'all' && stage === 'all' && billing === 'all' && selectedSPs.size === 0}
              onClick={resetFilters}>
              Réinitialiser les filtres
            </button>
</section></ClientFilters>}

        {situation && <aside aria-hidden={!filtersOpen} inert={filtersOpen ? undefined : ''} style={{ visibility: filtersOpen ? 'visible' : 'hidden' }} className={`ticket-situation__filter-rail${filtersOpen ? ' is-open' : ''}`} aria-label="Filtres de la situation">
          <section className="ticket-situation__inspector">
            <header><h2>Filtres et regroupement</h2><button type="button" onClick={resetFilters}>Réinitialiser</button></header>
            <div className="ticket-filter-section"><label className="ticket-situation__search ticket-situation__search--inside"><Search size={18} aria-hidden="true" /><span className="sr-only">Recherche</span><input aria-label="Rechercher une User Story" type="search" placeholder="Rechercher" value={searchQuery} onChange={event => setSearchQuery(event.target.value)} /></label></div>
            <div className="ticket-filter-section ticket-filter-group-card" aria-label="Options de regroupement">
              <h3>Regroupement</h3>
              <div className="ticket-group-card__choices" role="group" aria-label="Regrouper par">{[{value:'none',label:'Aucun'},{value:'day',label:'Jour'},{value:'week',label:'Semaine'},{value:'month',label:'Mois'},{value:'year',label:'Année'}].map(option => <button key={option.value} type="button" aria-pressed={groupBy === option.value} onClick={() => setGroupBy(option.value)}>{option.label}</button>)}</div>
              <label className="ticket-filter-select ticket-group-date">Date de regroupement<select aria-label="Date de regroupement" value={groupDateField} onChange={event => setGroupDateField(event.target.value)}><option value="dateBacklog">Date backlog</option><option value="dateDone">Date done</option></select></label>
            </div>
            {isAdmin && <div className="ticket-filter-section">
              <h3>Facturée</h3>
              <div className="ticket-filter-segment ticket-filter-segment--three" role="group" aria-label="Facturée">{[{value:'all',label:'Toutes'},{value:'billed',label:'Oui'},{value:'unbilled',label:'Non'}].map(option => <button key={option.value} type="button" aria-pressed={billing === option.value} onClick={() => setBilling(option.value)}>{option.label}</button>)}</div>
              <select className="sr-only" aria-label="Facturation" value={billing} onChange={event => setBilling(event.target.value)}><option value="all">Toutes</option><option value="billed">Facturées</option><option value="unbilled">Non facturées</option></select>
            </div>}
            <FilterSelectionCard title="Charge estimée" options={storyPointRangeOptions.map(range => ({ value: range.value, label: range.label, count: range.count }))} selected={selectedSPRanges} onToggle={toggleSPRange} emptyLabel="Aucune estimation" compact />
            <FilterSelectionCard title="Jalons proches" options={sortedMilestones.map(({ name, count }) => ({ value: name, label: name, count }))} selected={selectedMilestones} onToggle={toggleSituationMilestone} emptyLabel="Aucun jalon" visibleCount={3} />
            <div className="ticket-filter-section">
              <h3>Affichage</h3>
              <label className="ticket-filter-row"><span>Créateur</span><select aria-label="Créateur" value={creator} onChange={event => setCreator(event.target.value)}><option value="all">Tous</option><option value="me">Moi</option>{[...new Map(userStories.filter(us => us.creatorId).map(us => [us.creatorId, us.creatorName])).entries()].map(([id, name]) => <option key={id} value={id}>{name}</option>)}</select></label>
            </div>
            <div className="ticket-save-actions">
              {hasSavedFilter && <button type="button" className="ticket-load-filter" onClick={reloadSavedFilter}>Charger le filtre enregistré</button>}
              <button type="button" aria-live="polite" className={`ticket-save-view${filterSaveState === 'saved' ? ' is-saved' : ''}`} onClick={saveCurrentFilter}>{filterSaveState === 'saved' ? 'Filtre enregistré' : filterSaveState === 'error' ? 'Enregistrement impossible' : 'Enregistrer le filtre'}</button>
            </div>
          </section>
        </aside>}

        {situation && <button type="button" className="ticket-situation__filter-toggle" aria-expanded={filtersOpen} onClick={() => setFiltersOpen(open => !open)}><BarChart2 size={17} />{filtersOpen ? 'Masquer les filtres' : 'Afficher les filtres'}</button>}

        {/* ── Vue Done et situation : KPIs + Graphique + Groupement ── */}
        {showDoneLayout && doneKpis && (
          <>
            {/* KPI Cards */}
            <div className={situation ? 'ticket-situation__summary' : 'grid grid-cols-2 sm:grid-cols-4 gap-4 mb-6'}>
              <div className="bg-white rounded-xl border border-gray-200 px-5 py-4 flex items-center gap-4">
                <div className="w-10 h-10 rounded-lg bg-brand-green-lighter flex items-center justify-center flex-shrink-0">
                  <CheckCircle className="w-5 h-5 text-brand-green" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-gray-900">{doneKpis.total}</p>
                  <p className="text-xs text-gray-500 mt-0.5">{status === 'All' ? 'User Stories' : 'US livrées'}</p>
                </div>
              </div>
              <div className="bg-white rounded-xl border border-gray-200 px-5 py-4 flex items-center gap-4">
                <div className="w-10 h-10 rounded-lg bg-brand-purple-lighter flex items-center justify-center flex-shrink-0">
                  <Zap className="w-5 h-5 text-brand-purple" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-gray-900">{doneKpis.totalSP}</p>
                  <p className="text-xs text-gray-500 mt-0.5">{status === 'All' ? 'Story Points' : 'Story Points livrés'}</p>
                </div>
              </div>
              <div className="bg-white rounded-xl border border-gray-200 px-5 py-4 flex items-center gap-4">
                <div className="w-10 h-10 rounded-lg bg-blue-50 flex items-center justify-center flex-shrink-0">
                  <Clock className="w-5 h-5 text-blue-500" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-gray-900">{doneKpis.totalHours}h</p>
                  <p className="text-xs text-gray-500 mt-0.5">Heures estimées</p>
                </div>
              </div>
              <div className="bg-white rounded-xl border border-gray-200 px-5 py-4 flex items-center gap-4">
                <div className="w-10 h-10 rounded-lg bg-orange-50 flex items-center justify-center flex-shrink-0">
                  <TrendingUp className="w-5 h-5 text-orange-500" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-gray-900">{doneKpis.avgSP}</p>
                  <p className="text-xs text-gray-500 mt-0.5">SP moyen / US</p>
                </div>
              </div>
            </div>

            {/* Graphique */}
            {groupBy !== 'none' && chartData.length > 0 && <div className="bg-white rounded-xl border border-gray-200 mb-6 overflow-hidden">
              {/* Graphique */}
              {groupBy !== 'none' && chartData.length > 0 && (
                <div className="px-5 py-4">
                  <p className="text-xs font-medium text-gray-500 mb-3">
                    {status === 'All' ? 'Heures estimées' : 'Heures livrées'} {{ day: 'par jour', week: 'par semaine', month: 'par mois', year: 'par année' }[groupBy]} · {groupDateField === 'dateBacklog' ? 'Date backlog' : 'Date done'}
                  </p>
                  <ResponsiveContainer width="100%" height={180}>
                    <BarChart data={chartData} margin={{ top: 4, right: 8, left: -10, bottom: 0 }}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" vertical={false} />
                      <XAxis dataKey="name" tick={{ fontSize: 11, fill: '#6b7280' }} tickLine={false} axisLine={false} />
                      <YAxis tick={{ fontSize: 11, fill: '#6b7280' }} tickLine={false} axisLine={false} unit="h" />
                      <Tooltip
                        contentStyle={{ fontSize: 12, border: '1px solid #e5e7eb', borderRadius: 8 }}
                        formatter={(val, name) => [name === 'hours' ? `${val}h` : val, name === 'hours' ? 'Heures' : name === 'sp' ? 'SP' : 'US']}
                      />
                      <Bar dataKey="hours" fill="#4A9D7E" radius={[4, 4, 0, 0]} maxBarSize={48} />
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              )}
            </div>}
          </>
        )}

        {/* Liste des User Stories */}
        {filteredStories.length === 0 ? (
          <div key={`empty-${resultsAnimationKey}`} className="filter-results-motion bg-white rounded-lg border border-gray-200 p-12 text-center">
            <h3 className="text-lg font-medium text-gray-900 mb-2">
              Aucune User Story
            </h3>
            <p className="text-sm text-gray-500">
              {searchQuery || selectedMilestone !== 'all' || selectedMilestones.size > 0 || billing !== 'all' || creator !== 'all' || stage !== 'all'
                ? 'Essayez de modifier vos critères de recherche ou de filtre.'
                : `Aucune user story dans ${status === 'Confirmation' ? 'Confirmation' : statusLabel || status}.`}
            </p>
          </div>
        ) : situation && !groupedStories ? (
          <section key={`situation-${resultsAnimationKey}`} className="ticket-list filter-results-motion" aria-label="Liste des User Stories">
            <header className="ticket-list__header">
              <div><strong>{doneKpis.total}</strong><span>US</span><strong>{doneKpis.totalSP.toLocaleString('fr-FR')}</strong><span>SP</span><strong>{doneKpis.totalHours.toLocaleString('fr-FR')}</strong><span>h</span></div>
              <button type="button" onClick={() => setSortDate(value => value === 'desc' ? 'asc' : 'desc')}>Trié par date done {sortDate === 'desc' ? <ArrowDown size={14} /> : <ArrowUp size={14} />}</button>
            </header>
            <div className="ticket-list__rows">
              {paginatedStories.map((us, index) => {
                const usCode = us.code || `US-${String(index + 1).padStart(2, '0')}`;
                const stageLabel = displayLabel(us.stageName) || 'Sans étape';
                const tone = /done|termin/i.test(us.stageName || us.category || '') ? 'done' : /test/i.test(us.stageName || us.category || '') ? 'testing' : 'neutral';
                return <article key={us.id} className="ticket-list__row" role="button" tabIndex={0} onClick={() => openViewModal(us)} onKeyDown={event => { if (event.key === 'Enter' || event.key === ' ') openViewModal(us); }}>
                  <div className="ticket-list__story">
                    <div className="ticket-list__meta"><span>{usCode}</span><i>•</i><span>{us.milestoneName || 'Sans jalon'}</span><em className={`is-${tone}`}>{stageLabel}</em>{isAdmin && <UserStoryBillingBadge story={us} />}</div>
                    <h3>{us.name}</h3>
                    {us.category === 'Confirmation' && (isAdmin || us.canDecide) && (() => {
                      const isAccepted = validatedIds.has(us.id) || us.clientValidated;
                      const isBusy = validatingId === us.id || rejectingId === us.id;
                      return <div className="ticket-list__actions">
                        {!isAccepted && <button
                          type="button"
                          onClick={(event) => handleToggleValidation(event, us.id)}
                          disabled={isBusy}
                          className="is-accept"
                        >
                          <CheckCircle size={14} aria-hidden="true" />
                          {isBusy ? 'En cours...' : 'Accepter'}
                        </button>}
                        <button
                          type="button"
                          onClick={(event) => openRejectModal(event, us)}
                          disabled={isBusy}
                          className="is-reject"
                        >
                          <XCircle size={14} aria-hidden="true" />
                          {isBusy ? 'En cours...' : 'Refuser'}
                        </button>
                      </div>;
                    })()}
                  </div>
                  <div className="ticket-list__numbers"><strong>{us.storyPoints || 0} SP</strong><span>{us.dateDone ? new Date(us.dateDone).toLocaleDateString('fr-FR', { day: 'numeric', month: 'short' }) : '—'}</span></div>
                  <ChevronRight className="ticket-list__chevron" size={18} aria-hidden="true" />
                </article>;
              })}
            </div>
            {filteredStories.length > itemsPerPage && <Pagination currentPage={currentPage} totalPages={totalPages} totalItems={filteredStories.length} itemsPerPage={itemsPerPage} onPageChange={setCurrentPage} />}
          </section>
        ) : groupedStories ? (
          /* ── Vue groupée (Done par mois/semaine) ── */
          <div key={`grouped-${resultsAnimationKey}`} className="filter-results-motion space-y-3">
            {groupedStories.map(group => {
              const isCollapsed = collapsedGroups.has(group.key);
              return (
                <div key={group.key} className="bg-white rounded-lg border border-gray-200 overflow-hidden">
                  {/* En-tête du groupe */}
                  <button
                    onClick={() => toggleGroup(group.key)}
                    className="w-full flex items-center justify-between px-5 py-3 hover:bg-gray-50 transition-colors"
                  >
                    <div className="flex items-center gap-3">
                      {isCollapsed ? <ChevronRight className="w-4 h-4 text-gray-400" /> : <ChevronDown className="w-4 h-4 text-gray-400" />}
                      <CalendarDays className="w-4 h-4 text-brand-green" />
                      <span className="text-sm font-semibold text-gray-800">{group.label}</span>
                    </div>
                    <div className="flex items-center gap-4 text-xs">
                      <span className="text-gray-500">{group.stories.length} US</span>
                      <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full bg-brand-green-lighter text-brand-green-dark font-semibold">
                        <Zap className="w-3 h-3" />{group.totalSP} SP · {group.totalHours}h
                      </span>
                    </div>
                  </button>

                  {/* Lignes du groupe */}
                  {!isCollapsed && (
                    <>
                      <div className="bg-gray-50 border-t border-gray-100 border-b border-gray-200 px-5 py-2">
                        <div className="flex items-start gap-4">
                          <div className="w-32"><span className="text-xs font-semibold text-gray-500 uppercase tracking-wide">Numéro</span></div>
                          <div className="w-48"><span className="text-xs font-semibold text-gray-500 uppercase tracking-wide">Jalon</span></div>
                          <div className="flex-1"><span className="text-xs font-semibold text-gray-500 uppercase tracking-wide">Titre</span></div>
                          <div className="w-32 text-center"><span className="text-xs font-semibold text-gray-500 uppercase tracking-wide">Date backlog</span></div>
                          <div className="w-32 text-center">
                            <button onClick={() => setSortDate(prev => prev === 'desc' ? 'asc' : 'desc')} className="inline-flex items-center gap-1 text-xs font-semibold text-gray-500 uppercase tracking-wide hover:text-brand-green transition-colors">
                              Date done
                              {sortDate === 'desc' ? <ArrowDown className="w-3.5 h-3.5 text-brand-green" /> : <ArrowUp className="w-3.5 h-3.5 text-brand-green" />}
                            </button>
                          </div>
                          <div className="w-28 text-center"><span className="text-xs font-semibold text-gray-500 uppercase tracking-wide">Estimation</span></div>
                          <div className="w-24 text-center"><span className="text-xs font-semibold text-gray-500 uppercase tracking-wide">Durée</span></div>
                        </div>
                      </div>
                      <div className="divide-y divide-gray-100">
                        {group.stories.map((us, idx) => {
                          const usCode = us.code || `US-${String(idx + 1).padStart(2, '0')}`;
                          return (
                            <div key={us.id} className="px-5 py-3 hover:bg-gray-50 transition-colors cursor-pointer" onClick={() => openViewModal(us)}>
                              <div className="flex items-start gap-4">
                                <div className="w-32 pt-0.5"><span className="text-xs font-semibold text-gray-500">{usCode}</span></div>
                                <div className="w-48 min-w-0 pt-0.5">
                                  {us.milestoneName
                                    ? <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-purple-100 text-purple-700">{us.milestoneName}</span>
                                    : <span className="text-xs text-gray-400">—</span>}
                                </div>
                                <div className="flex-1 min-w-0"><h3 className="text-sm font-medium text-gray-900">{us.name}</h3>
                        {isAdmin && <UserStoryBillingBadge story={us} />}{situation && <div className="mt-1.5 flex flex-wrap items-center gap-2 text-xs text-gray-500"><span className="inline-flex rounded-full bg-purple-100 px-2 py-0.5 font-medium text-purple-700">{displayLabel(us.stageName) || 'Sans étape'}</span><span>{us.creatorName || '—'}</span></div>}
                                  {situation && us.category === 'Confirmation' && (isAdmin || us.canDecide) && (() => {
                                    const isAccepted = validatedIds.has(us.id) || us.clientValidated;
                                    const isBusy = validatingId === us.id || rejectingId === us.id;
                                    return <div className="ticket-list__actions">
                                      {!isAccepted && <button type="button" className="is-accept" disabled={isBusy} onClick={(event) => handleToggleValidation(event, us.id)}><CheckCircle size={14} aria-hidden="true" />{isBusy ? 'En cours...' : 'Accepter'}</button>}
                                      <button type="button" className="is-reject" disabled={isBusy} onClick={(event) => openRejectModal(event, us)}><XCircle size={14} aria-hidden="true" />{isBusy ? 'En cours...' : 'Refuser'}</button>
                                    </div>;
                                  })()}
                                </div>
                                <div className="w-32 text-center pt-0.5">
                                  <span className="text-xs text-gray-600">
                                    {us.dateBacklog ? new Date(us.dateBacklog).toLocaleDateString('fr-FR', { day: '2-digit', month: 'short', year: 'numeric' }) : '—'}
                                  </span>
                                </div>
                                <div className="w-32 text-center pt-0.5">
                                  <span className="text-xs text-gray-600">
                                    {us.dateDone ? new Date(us.dateDone).toLocaleDateString('fr-FR', { day: '2-digit', month: 'short', year: 'numeric' }) : '—'}
                                  </span>
                                </div>
                                <div className="w-28 text-center pt-0.5">
                                  <span className="inline-flex items-center justify-center px-2 py-1 rounded-full text-xs font-semibold bg-brand-purple-lighter text-brand-purple">
                                    {us.storyPoints || 0} SP
                                  </span>
                                </div>
                                <div className="w-24 text-center pt-0.5">
                                  <span className="inline-flex items-center justify-center px-2 py-1 rounded-full text-xs font-semibold bg-brand-green-lighter text-brand-green-dark">
                                    {us.hourlyRate || 0}h
                                  </span>
                                </div>
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    </>
                  )}
                </div>
              );
            })}
          </div>
        ) : (
          /* ── Vue liste classique ── */
          <div key={`list-${resultsAnimationKey}`} className="filter-results-motion bg-white rounded-lg border border-gray-200 overflow-hidden">
            {/* En-tête du tableau */}
            <div className="bg-gray-50 border-b border-gray-200 px-6 py-3">
              <div className="flex items-start gap-4">
                <div className="w-32">
                  <span className="text-xs font-semibold text-gray-600 uppercase tracking-wide">
                    Numéro
                  </span>
                </div>
                <div className="w-48">
                  <span className="text-xs font-semibold text-gray-600 uppercase tracking-wide">
                    Jalon
                  </span>
                </div>
                <div className="flex-1">
                  <span className="text-xs font-semibold text-gray-600 uppercase tracking-wide">
                    Titre
                  </span>
                </div>
                <div className="w-32 text-center">
                  <span className="text-xs font-semibold text-gray-600 uppercase tracking-wide">Date backlog</span>
                </div>
                <div className="w-32 text-center">
                  <button onClick={() => setSortDate(prev => prev === 'desc' ? 'asc' : 'desc')} className="inline-flex items-center gap-1 text-xs font-semibold text-gray-600 uppercase tracking-wide hover:text-brand-green transition-colors">
                    Date done
                    {sortDate === 'desc' ? <ArrowDown className="w-3.5 h-3.5 text-brand-green" /> : <ArrowUp className="w-3.5 h-3.5 text-brand-green" />}
                  </button>
                </div>
                <div className="w-28 text-center">
                  <button
                    onClick={() => setSortSP(prev => prev === 'none' ? 'asc' : prev === 'asc' ? 'desc' : 'none')}
                    className="inline-flex items-center gap-1 text-xs font-semibold text-gray-600 uppercase tracking-wide hover:text-brand-green transition-colors"
                  >
                    Estimation
                    {sortSP === 'none' && <ArrowUpDown className="w-3.5 h-3.5 text-gray-400" />}
                    {sortSP === 'asc' && <ArrowUp className="w-3.5 h-3.5 text-brand-green" />}
                    {sortSP === 'desc' && <ArrowDown className="w-3.5 h-3.5 text-brand-green" />}
                  </button>
                </div>
                <div className="w-24 text-center">
                  <span className="text-xs font-semibold text-gray-600 uppercase tracking-wide">Durée</span>
                </div>
                {status === 'Confirmation' && (
                  <div className="w-56 text-center">
                    <span className="text-xs font-semibold text-gray-600 uppercase tracking-wide">
                      Actions
                    </span>
                  </div>
                )}
              </div>
            </div>

            {/* Liste des US */}
            <div className="divide-y divide-gray-200">
              {paginatedStories.map((us, index) => {
                const usCode = us.code || `US-${String(index + 1).padStart(2, '0')}`;

                return (
                  <div
                    key={us.id}
                    className={`px-6 py-4 transition-colors cursor-pointer ${
                      (us.stageId === 397 || rejectedIds.has(us.id))
                        ? 'bg-red-50/50 hover:bg-red-50 border-l-4 border-l-red-300'
                        : 'hover:bg-gray-50'
                    }`}
                    onClick={() => openViewModal(us)}
                  >
                    <div className="flex items-start gap-4">
                      {/* Numéro */}
                      <div className="w-32 pt-0.5">
                        <span className="text-xs font-semibold text-gray-500">
                          {usCode}
                        </span>
                      </div>

                      {/* Jalon */}
                      <div className="w-48 min-w-0 pt-0.5">
                        {us.milestoneName ? (
                          <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-purple-100 text-purple-700">
                            {us.milestoneName}
                          </span>
                        ) : (
                          <span className="text-xs text-gray-400">—</span>
                        )}
                      </div>

                      {/* Titre */}
                      <div className="flex-1 min-w-0">
                        <h3 className={`text-sm font-medium ${
                          (us.stageId === 397 || rejectedIds.has(us.id))
                            ? 'line-through text-gray-400'
                            : 'text-gray-900'
                        }`}>
                          {us.name}
                        </h3>
                        {isAdmin && <UserStoryBillingBadge story={us} />}
                        {situation && <div className="mt-1.5 flex flex-wrap items-center gap-2 text-xs text-gray-500"><span className="inline-flex rounded-full bg-purple-100 px-2 py-0.5 font-medium text-purple-700">{displayLabel(us.stageName) || 'Sans étape'}</span><span>{us.creatorName || '—'}</span></div>}
                      </div>

                      {/* Date backlog */}
                      <div className="w-32 text-center pt-0.5">
                        <span className="text-xs text-gray-600">
                          {us.dateBacklog ? new Date(us.dateBacklog).toLocaleDateString('fr-FR', { day: '2-digit', month: 'short', year: 'numeric' }) : '—'}
                        </span>
                      </div>

                      {/* Date done */}
                      <div className="w-32 text-center pt-0.5">
                        <span className="text-xs text-gray-600">
                          {us.dateDone ? new Date(us.dateDone).toLocaleDateString('fr-FR', { day: '2-digit', month: 'short', year: 'numeric' }) : '—'}
                        </span>
                      </div>

                      {/* Estimation (SP) */}
                      <div className="w-28 text-center pt-0.5">
                        <span className="inline-flex items-center justify-center px-2 py-1 rounded-full text-xs font-semibold bg-brand-purple-lighter text-brand-purple">
                          {us.storyPoints || 0} SP
                        </span>
                      </div>

                      {/* Durée (heures) */}
                      <div className="w-24 text-center pt-0.5">
                        <span className="inline-flex items-center justify-center px-2 py-1 rounded-full text-xs font-semibold bg-brand-green-lighter text-brand-green-dark">
                          {us.hourlyRate || 0}h
                        </span>
                      </div>

                      {/* Boutons Accepter / Refuser (Confirmation uniquement) */}
                      {status === 'Confirmation' && (isAdmin || us.canDecide) && (() => {
                        const isRefused  = us.stageId === 397 || rejectedIds.has(us.id);
                        const isAccepted = validatedIds.has(us.id) || us.clientValidated;
                        const isBusy     = validatingId === us.id || rejectingId === us.id;

                        if (isRefused) {
                          return (
                            <div className="w-56 flex items-center justify-center pt-0.5">
                              <button
                                onClick={(e) => handleAcceptRefused(e, us)}
                                disabled={isBusy}
                                className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-semibold bg-green-600 text-white hover:bg-green-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                              >
                                {isBusy ? <><div className="animate-spin rounded-full h-3 w-3 border-b-2 border-white"></div>En cours...</> : <><CheckCircle className="w-3.5 h-3.5" />Accepter</>}
                              </button>
                            </div>
                          );
                        }

                        if (isAccepted) {
                          return (
                            <div className="w-56 flex items-center justify-center pt-0.5">
                              <button
                                onClick={(e) => openRejectModal(e, us)}
                                disabled={isBusy}
                                className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-semibold bg-red-50 text-red-600 hover:bg-red-100 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                              >
                                {isBusy ? <><div className="animate-spin rounded-full h-3 w-3 border-b-2 border-red-600"></div>En cours...</> : <><XCircle className="w-3.5 h-3.5" />Refuser</>}
                              </button>
                            </div>
                          );
                        }

                        return (
                          <div className="w-56 flex items-center justify-center gap-2 pt-0.5">
                            <button
                              onClick={(e) => handleToggleValidation(e, us.id)}
                              disabled={isBusy}
                              className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-semibold bg-green-600 text-white hover:bg-green-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                            >
                              {isBusy ? <><div className="animate-spin rounded-full h-3 w-3 border-b-2 border-white"></div>En cours...</> : <><CheckCircle className="w-3.5 h-3.5" />Accepter</>}
                            </button>
                            <button
                              onClick={(e) => openRejectModal(e, us)}
                              disabled={isBusy}
                              className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-semibold bg-red-50 text-red-600 hover:bg-red-100 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                            >
                              {isBusy ? <><div className="animate-spin rounded-full h-3 w-3 border-b-2 border-red-600"></div>En cours...</> : <><XCircle className="w-3.5 h-3.5" />Refuser</>}
                            </button>
                          </div>
                        );
                      })()}
                    </div>
                  </div>
                );
              })}
            </div>

            {/* Pagination */}
            {filteredStories.length > itemsPerPage && (
              <Pagination
                currentPage={currentPage}
                totalPages={totalPages}
                totalItems={filteredStories.length}
                itemsPerPage={itemsPerPage}
                onPageChange={setCurrentPage}
              />
            )}
          </div>
        )}

        {/* Modale de refus */}
        {rejectingUs && (
          <div className={`client-story-dialog ${rejectModalExit.modalClassName} fixed inset-0 bg-black/50 z-[70] flex items-center justify-center p-6`} onClick={rejectModalExit.close}>
            <div
              className="bg-white rounded-lg w-full max-w-lg shadow-2xl"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="px-6 py-4 border-b border-gray-200 flex items-start justify-between gap-4">
                <div className="flex items-center gap-3">
                  <div className="w-9 h-9 rounded-full bg-red-100 flex items-center justify-center flex-shrink-0">
                    <AlertCircle className="w-5 h-5 text-red-600" />
                  </div>
                  <div>
                    <h2 className="text-base font-semibold text-gray-900">Refuser la User Story</h2>
                    <p className="text-sm text-gray-500 mt-0.5">{rejectingUs.code} — {rejectingUs.name}</p>
                  </div>
                </div>
                <button onClick={rejectModalExit.close} className="text-gray-400 hover:text-gray-600 p-1">
                  <XCircle className="w-5 h-5" />
                </button>
              </div>
              <div className="px-6 py-5">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Motif de refus <span className="text-red-500">*</span>
                </label>
                <textarea
                  value={rejectFeedback}
                  onChange={(e) => setRejectFeedback(e.target.value)}
                  placeholder="Expliquez pourquoi cette US est refusée..."
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-red-500 resize-none text-sm"
                  rows="4"
                  autoFocus
                />
              </div>
              <div className="px-6 py-4 border-t border-gray-200 flex items-center justify-end gap-3 bg-gray-50 rounded-b-lg">
                <button
                  onClick={rejectModalExit.close}
                  className="px-4 py-2 text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors text-sm font-medium"
                >
                  Annuler
                </button>
                <button
                  onClick={handleConfirmReject}
                  disabled={!rejectFeedback.trim() || rejectingId === rejectingUs.id}
                  className="flex items-center gap-2 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors text-sm font-medium disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {rejectingId === rejectingUs.id ? (
                    <>
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                      Envoi...
                    </>
                  ) : (
                    <>
                      <XCircle className="w-4 h-4" />
                      Confirmer le refus
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Modale de détails */}
        {viewingUs && (
          <div className={`us-detail-overlay client-story-dialog ${viewModalExit.modalClassName} fixed inset-0 bg-black/50 z-[60] flex items-center justify-center p-6`} onClick={viewModalExit.close}>
            <div
              className="bg-white rounded-lg us-detail-window w-full overflow-hidden flex flex-col shadow-2xl"
              onClick={(e) => e.stopPropagation()}
              role="dialog"
              aria-modal="true"
              aria-labelledby="user-story-detail-title"
            >
              {/* Header */}
              <div className="px-6 py-4 border-b border-gray-200 flex-shrink-0">
                <div className="flex items-start justify-between gap-4">
                  <h2 id="user-story-detail-title" className="text-xl font-semibold text-gray-900 flex-1">
                    {viewingUs.name}
                  </h2>
                  {isAdmin && <UserStoryBillingBadge story={viewingUs} />}
                  <button
                    onClick={viewModalExit.close}
                    className="text-gray-400 hover:text-gray-600 transition-colors p-2 hover:bg-gray-100 rounded-lg"
                  >
                    <XCircle className="w-6 h-6" />
                  </button>
                </div>
              </div>

              {/* Corps : 2 colonnes */}
              <div className="client-story-dialog-body">
                {/* Colonne gauche — Détails US */}
                <div className="flex-1 overflow-y-auto px-6 py-6">
                  {/* Numéro et Jalon */}
                  <div className="mb-6 grid grid-cols-3 gap-6">
                    <div>
                      <h3 className="text-sm font-medium text-gray-500 uppercase tracking-wide mb-3">
                        Numéro
                      </h3>
                      <div className="text-base text-gray-700 font-semibold">
                        {viewingUs.code || viewingUs.id}
                      </div>
                    </div>

                    {viewingUs.milestoneName && (
                      <div>
                        <h3 className="text-sm font-medium text-gray-500 uppercase tracking-wide mb-3">
                          Jalon
                        </h3>
                        <span className="inline-flex items-center px-2.5 py-1 rounded-full text-sm font-medium bg-purple-100 text-purple-700">
                          {viewingUs.milestoneName}
                        </span>
                      </div>
                    )}
                  </div>

                  <AcceptanceCriteriaGrid initialData={chatter.criteriaData} initialError={chatter.criteriaData ? null : chatter.error} task={viewingUs} fallback={viewingUs.description ? (
                    <div className="mb-6">
                      <h3 className="text-sm font-medium text-gray-500 uppercase tracking-wide mb-3">
                        Objet du test
                      </h3>
                      <div
                        className="text-base text-gray-700 prose prose-base max-w-none bg-gray-50 rounded-lg p-6 border border-gray-200"
                        dangerouslySetInnerHTML={{ __html: viewingUs.description }}
                      />
                    </div>
                  ) : null} />
                </div>

                {/* Colonne droite — Chatter */}
                <div className="us-chatter-pane w-[520px] bg-gray-50 flex flex-col border-l border-gray-200">
                  <div className="px-6 pb-4 bg-white border-b border-gray-200 flex-shrink-0"><UserStoryAttachments taskId={viewingUs.id} /></div>
                    {/* Header chatter */}
                    <div className="px-6 py-4 border-b border-gray-200 bg-white flex-shrink-0">
                      <div className="flex items-center gap-2 mb-1">
                        <MessageSquare className="w-5 h-5 text-brand-purple" />
                        <h3 className="text-base font-bold text-gray-900">Chatter</h3>
                        <span className="text-sm text-gray-500">({userStoryMessages.length} sur cette page)</span>
                      </div>
                      <p className="text-sm text-gray-500">Historique des messages</p>
                    </div>

                    {/* Liste des messages */}
                    <div className="p-4 bg-white border-t border-gray-200 flex-shrink-0">
                      <form onSubmit={handleSendUserStoryMessage} className="flex flex-col gap-2">
                        <textarea
                          value={newUserStoryMessage}
                          onChange={(e) => setNewUserStoryMessage(e.target.value)}
                          placeholder="Écrire un commentaire..."
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-brand-purple focus:border-brand-purple resize-none text-sm"
                          rows="3"
                          disabled={isSendingUserStoryMessage}
                        />
                        <div className="flex justify-end">
                          <button
                            type="submit"
                            disabled={isSendingUserStoryMessage || !newUserStoryMessage.trim()}
                            className="flex items-center gap-2 px-4 py-2 bg-brand-purple text-white rounded-lg hover:bg-brand-purple-dark transition-colors font-medium text-sm disabled:opacity-50 disabled:cursor-not-allowed"
                          >
                            {isSendingUserStoryMessage ? (
                              <>
                                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                                Envoi...
                              </>
                            ) : (
                              <>
                                <Send className="w-4 h-4" />
                                Envoyer
                              </>
                            )}
                          </button>
                        </div>
                      </form>
                    </div>
                  <ChatterPagination chatter={chatter} />
                  <div className="chatter-message-list flex-1 overflow-y-auto p-6 pb-0">
                      {loadingUsMessages ? (
                        <div className="flex items-center justify-center py-16">
                          <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-brand-purple"></div>
                        </div>
                      ) : userStoryMessages.length === 0 ? (
                        <div className="text-center py-16 bg-white rounded border border-gray-200">
                          <MessageSquare className="w-16 h-16 text-gray-300 mx-auto mb-4" />
                          <p className="text-base text-gray-500 mb-2">Aucun message</p>
                          <p className="text-sm text-gray-400">Soyez le premier à commenter</p>
                        </div>
                      ) : (
                        <div className="space-y-4">
                          {userStoryMessages.map((message) => {
                            const isEmail = message.messageType === 'email';
                            return (
                              <div
                                key={message.id}
                                className={`rounded-lg p-4 border shadow-sm ${isEmail ? 'bg-blue-50 border-blue-200' : 'bg-white border-gray-200'}`}
                              >
                                <div className="flex items-start gap-3 mb-3">
                                  <div className={`w-9 h-9 rounded-full flex items-center justify-center flex-shrink-0 ${isEmail ? 'bg-blue-200' : 'bg-brand-purple-lighter'}`}>
                                    {isEmail ? (
                                      <span className="text-base">📧</span>
                                    ) : (
                                      <User className="w-4 h-4 text-brand-purple" />
                                    )}
                                  </div>
                                  <div className="flex-1 min-w-0">
                                    <p className="text-sm font-bold text-gray-900 truncate">{message.authorName}</p>
                                    <p className="text-sm text-gray-500">
                                      {message.date && new Date(message.date).toLocaleString('fr-FR', {
                                        day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit'
                                      })}
                                    </p>
                                  </div>
                                </div>

                                {message.body && (
                                  <div
                                    className="text-sm text-gray-700 prose prose-sm max-w-none"
                                    dangerouslySetInnerHTML={{ __html: message.body }}
                                  />
                                )}
                              </div>
                            );
                          })}
                        </div>
                      )}
                    </div>

                    {/* Formulaire d'envoi */}


                </div>
              </div>

              {/* Footer */}
              <footer className="us-detail-footer">
                <button onClick={viewModalExit.close} className="us-detail-footer__close">Fermer</button>
                {(isAdmin || viewingUs.canDecide) && (viewingUs.category === 'Confirmation' || (status === 'Confirmation' && (viewingUs.stageId === 397 || rejectedIds.has(viewingUs.id)))) && (() => {
                  const isAccepted = validatedIds.has(viewingUs.id) || viewingUs.clientValidated;
                  const isRefused = !isAccepted && (viewingUs.stageId === 397 || rejectedIds.has(viewingUs.id));
                  const isBusy = validatingId === viewingUs.id || rejectingId === viewingUs.id;
                  return <div className="us-detail-footer__actions">
                    {!isAccepted && <button
                      type="button"
                      onClick={(event) => isRefused ? handleAcceptRefused(event, viewingUs) : handleToggleValidation(event, viewingUs.id)}
                      disabled={isBusy}
                      className="flex items-center gap-2 px-4 py-2.5 bg-green-600 hover:bg-green-700 text-white rounded-lg font-medium transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      <CheckCircle className="w-4 h-4" />
                      {isBusy ? 'En cours...' : 'Accepter'}
                    </button>}
                    {!isRefused && <button
                      type="button"
                      onClick={(event) => openRejectModal(event, viewingUs)}
                      disabled={isBusy}
                      className="flex items-center gap-2 px-4 py-2.5 bg-red-50 hover:bg-red-100 text-red-700 rounded-lg font-medium transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      <XCircle className="w-4 h-4" />
                      {isBusy ? 'En cours...' : 'Refuser'}
                    </button>}
                  </div>;
                })()}
              </footer>
            </div>
          </div>
        )}
      </div>
    </ClientPage>
  );
};

export default UserStoriesByStatusPage;
